import React from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { usePriceRowContext } from '../../../price-row-context';

import Clickable from '@prisjakt/ui-clickable';

import { MobileOnly, MediumUp } from 'src/ui/block';
import { Truncated } from 'src/components/truncated';
import OptionsColumn from '../../row-types/alternative-price-row/options-cloumn';
import { DeliveryTime } from '../delivery-time';
import { ExpandButton } from '../expand-button';
import { GoToStoreButton } from '../go-to-store-button';
import { PriceInfoExperiment } from '../price-info-experiment';
import { StockPriceInfo } from '../stock-price-info';
import { StoreInfoColumn } from '../store-info-column';

import { getContainerProps } from '../../utils';

import { ExperimentOfferItemEnum } from 'src/hooks/experiments';

import { AlternativePrice } from 'src/types';
import { ITableLinkProps } from '../../types';

import {
  StyledActionArea,
  StyledCTAButton,
  StyledCTAWrapper,
  StyledDeliveryBadges,
  StyledEndColumn,
  StyledExpandButton,
  StyledMiddleColumn,
  StyledProductName,
  StyledRow,
  StyledProductNameColumn,
  StyledTopMiddleRow,
} from './styles';

type Props = {
  alternativeOffer?: AlternativePrice;
  expanded: boolean;
  handleExpandButtonClick?: (
    event: React.MouseEvent<Element, MouseEvent>
  ) => void;
  tableLinkProps: ITableLinkProps;
};

export const FeaturedColumns: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<Props>
>> = props => {
  const {
    alternativeOffer,
    expanded,
    handleExpandButtonClick,
    tableLinkProps,
  } = props;

  const {
    newOfferItemExperiment,
    offer,
    forceMobileView,
    showExpandButton,
  } = usePriceRowContext();
  const { t } = useAppContext();

  const isAlternative = Boolean(alternativeOffer);
  const isDownload = offer.priceType === 'download';
  const name = alternativeOffer?.name || offer.name;

  const externalUri = isAlternative
    ? alternativeOffer?.externalUri
    : offer.externalUri;

  const containerProps = getContainerProps(
    name || '',
    true,
    tableLinkProps.onClick,
    externalUri
  );
  const renderBaseline = () => {
    return (
      <StyledRow>
        {!isAlternative && <StoreInfoColumn />}
        <StyledMiddleColumn $featured>
          <StyledTopMiddleRow $featured $forceMobileView={forceMobileView}>
            <StyledProductNameColumn
              $forceMobileView={Boolean(forceMobileView)}
            >
              <StyledProductName
                $featured
                $forceMobileView={forceMobileView}
                $isAlternative={isAlternative}
              >
                {name}
              </StyledProductName>
            </StyledProductNameColumn>
            <StockPriceInfo alternativeOffer={alternativeOffer} />
          </StyledTopMiddleRow>
        </StyledMiddleColumn>
        <StyledEndColumn $forceMobileView={forceMobileView} $featured>
          <GoToStoreButton />
        </StyledEndColumn>
      </StyledRow>
    );
  };

  const renderExperiment = () => {
    return (
      <>
        <MobileOnly>
          <div>
            <StyledRow $newOfferItemExperiment>
              <PriceInfoExperiment alternativeOffer={alternativeOffer} />
              {!isAlternative && <StoreInfoColumn />}
              {alternativeOffer && (
                <OptionsColumn alternativeOffer={alternativeOffer} />
              )}
            </StyledRow>

            {!isDownload && (
              <StyledDeliveryBadges>
                <DeliveryTime
                  shipping={alternativeOffer?.shipping || offer.shipping}
                  stockStatus={
                    alternativeOffer?.stock?.status || offer.stock?.status
                  }
                />

                {!isAlternative && (
                  <StyledExpandButton onClick={handleExpandButtonClick}>
                    <ExpandButton
                      expanded={expanded}
                      title={t('product', 'price.more-info')}
                    />
                  </StyledExpandButton>
                )}
              </StyledDeliveryBadges>
            )}

            {(newOfferItemExperiment ===
              ExperimentOfferItemEnum.NEW_WITH_CTA_BUTTON ||
              isAlternative) && (
              <StyledCTAWrapper>
                {isAlternative && (
                  <StyledProductNameColumn $newOfferItemExperiment>
                    <Truncated>{name}</Truncated>
                  </StyledProductNameColumn>
                )}
                <StyledCTAButton isAlternative={isAlternative} />
              </StyledCTAWrapper>
            )}
          </div>
        </MobileOnly>

        <MediumUp>{renderBaseline()}</MediumUp>
      </>
    );
  };

  return (
    <StyledActionArea
      container={Clickable}
      containerProps={containerProps}
      style={{ paddingLeft: showExpandButton ? 0 : 48 }}
    >
      {newOfferItemExperiment ? renderExperiment() : renderBaseline()}
    </StyledActionArea>
  );
};
